// src/modules/app-views/toolbox/pages/Ai/components/SearchedTool.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Tag, Typography, Divider, Descriptions, Collapse, Grid, theme } from 'antd';
import {
  EditOutlined,
  DollarOutlined,
  TagsOutlined,
  ToolOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  BookOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import TypingText from 'modules/app-views/products/pages/Ai/components/TypingText';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import { formatPrice, buildNaturalIntro } from '../helpers';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * SearchedTool - Displays the AI cataloged tool details
 */
const SearchedTool = ({
  message,
  typingProgress,
  introTypingComplete,
  catalogedToolId,
  chatContainerRef,
  showAvatar = true,
  creditsInfo = {},
  onIntroComplete,
  onEditTool,
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { token } = theme.useToken();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const screens = Grid.useBreakpoint();

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const isMobile = !screens.md;

  const { tool, meta, creditsDebit, alreadyExists } = message;
  const tp = typingProgress;
  const cl = tool?.classification;
  const specs = tool?.specs;
  const pricing = tool?.pricing;
  const usage = tool?.usage;
  const maintenance = tool?.maintenance;
  const refs = tool?.references;

  // Generate natural language intro
  const naturalIntro = buildNaturalIntro(tool, alreadyExists);

  // Check if sections have data (!! ensures boolean, prevents rendering "0")
  const hasClassification = !!(cl && (cl.category || cl.subcategory || cl.toolType));
  const hasSpecs = !!(specs && (specs.toolType || specs.powerType || specs.precision || specs.esdSafe !== undefined));
  const hasPricing = !!(pricing && (pricing.averagePrice || pricing.priceRangeMin || pricing.whereToBuy));
  const hasUsage = !!(usage && (usage.commonServices?.length || usage.alternatives?.length));
  const hasMaintenance = !!(maintenance && (maintenance.expectedLifespan || maintenance.calibration || maintenance.maintenanceNotes));
  const hasReferences = !!(refs && (refs.videos?.length || refs.websites?.length || refs.searchTerms?.length));

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <div style={{ width: '100%' }}>
      {/* Natural language intro with typing effect */}
      <div style={{ marginBottom: 20, lineHeight: 1.6, fontSize: 15 }}>
        <TypingText
          text={naturalIntro}
          speed={10}
          onComplete={onIntroComplete}
          scrollContainerRef={chatContainerRef}
          style={{ color: 'var(--ant-color-text)' }}
        />
      </div>

      {/* Tool Header: Avatar + Title - shows after intro */}
      {introTypingComplete && tp.name && (
        <Flex
          vertical={isMobile}
          align={isMobile ? 'center' : 'flex-start'}
          gap={16}
          style={{ marginBottom: 20 }}
          className="fade-in"
        >
          {/* Tool avatar with deterministic colors (conditional based on config) */}
          {showAvatar && (
            <SimpleAvatar
              name={tool?.shortName || tool?.name}
              size={80}
              shape="square"
              backColor={tool?.backColor}
              foreColor={tool?.foreColor}
              style={{ borderRadius: 12, flexShrink: 0 }}
            />
          )}

          <Flex vertical justify="center" style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <div
              style={{
                fontSize: isMobile ? 18 : 20,
                fontWeight: 600,
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {tool?.shortName || tool?.name}
            </div>
            {tool?.shortName && tool?.name && tool?.shortName !== tool?.name && (
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                {tool.name}
              </Text>
            )}
            {tool?.brand && (
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4, marginTop: 2 }}>
                {translateX('brand')}: {tool.brand}
              </Text>
            )}
            {tool?.description && (
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4, marginTop: 4 }}>
                {tool.description}
              </Text>
            )}

            {/* Average price highlight */}
            {pricing?.averagePrice && (
              <Flex align="center" justify={isMobile ? 'center' : 'flex-start'} gap={8} style={{ marginTop: 12 }}>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: token.colorPrimary,
                  }}
                >
                  {formatPrice(pricing.averagePrice, true)}
                </div>
                {!alreadyExists && (
                  <Tag color="blue" style={{ margin: 0 }}>
                    {translateX('average_price')}
                  </Tag>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      )}

      {/* Classification Section */}
      {introTypingComplete && tp.classification && hasClassification && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['classification']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'classification',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <TagsOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('classification')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {cl.category && (
                    <Descriptions.Item label={translateX('category')}>
                      {cl.category}
                    </Descriptions.Item>
                  )}
                  {cl.subcategory && (
                    <Descriptions.Item label={translateX('subcategory')}>
                      {cl.subcategory}
                    </Descriptions.Item>
                  )}
                  {cl.toolType && (
                    <Descriptions.Item label={translateX('tool_type')}>
                      {cl.toolType}
                    </Descriptions.Item>
                  )}
                  {tool?.unitOfMeasure && (
                    <Descriptions.Item label={translateX('unit')}>
                      {tool.unitOfMeasure.unit}
                      {tool.unitOfMeasure.description && ` - ${tool.unitOfMeasure.description}`}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Specs Section */}
      {introTypingComplete && tp.specs && hasSpecs && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['specs']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'specs',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <SettingOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('specifications')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {specs.toolType && (
                    <Descriptions.Item label={translateX('tool_type')}>
                      {specs.toolType}
                    </Descriptions.Item>
                  )}
                  {specs.powerType && (
                    <Descriptions.Item label={translateX('power_type')}>
                      <Flex align="center" gap={6}>
                        <ThunderboltOutlined />
                        {specs.powerType}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {specs.precision && (
                    <Descriptions.Item label={translateX('precision')}>
                      {specs.precision}
                    </Descriptions.Item>
                  )}
                  {specs.esdSafe !== undefined && (
                    <Descriptions.Item label={translateX('esd_safe')}>
                      <Flex align="center" gap={6}>
                        <SafetyCertificateOutlined
                          style={{ color: specs.esdSafe ? '#52c41a' : '#ff4d4f' }}
                        />
                        {specs.esdSafe ? translateX('yes') : translateX('no')}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {specs.material && (
                    <Descriptions.Item label={translateX('material')}>
                      {specs.material}
                    </Descriptions.Item>
                  )}
                  {specs.dimensions && (
                    <Descriptions.Item label={translateX('dimensions')}>
                      {specs.dimensions}
                    </Descriptions.Item>
                  )}
                  {specs.weight && (
                    <Descriptions.Item label={translateX('weight')}>
                      {specs.weight}
                    </Descriptions.Item>
                  )}
                  {specs.specNotes && (
                    <Descriptions.Item label={translateX('notes')}>
                      {specs.specNotes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Pricing Section */}
      {introTypingComplete && tp.pricing && hasPricing && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['pricing']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'pricing',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <DollarOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('pricing_info')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {pricing.averagePrice !== undefined && (
                    <Descriptions.Item label={translateX('average_price')}>
                      {formatPrice(pricing.averagePrice, true)}
                    </Descriptions.Item>
                  )}
                  {(pricing.priceRangeMin !== undefined || pricing.priceRangeMax !== undefined) && (
                    <Descriptions.Item label={translateX('price_range')}>
                      {formatPrice(pricing.priceRangeMin, true)} - {formatPrice(pricing.priceRangeMax, true)}
                    </Descriptions.Item>
                  )}
                  {pricing.whereToBuy && (
                    <Descriptions.Item label={translateX('where_to_buy')}>
                      <Flex gap={4} wrap="wrap">
                        {Array.isArray(pricing.whereToBuy)
                          ? pricing.whereToBuy.map((place, i) => (
                              <Tag key={i}>{place}</Tag>
                            ))
                          : pricing.whereToBuy
                        }
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {pricing.priceNotes && (
                    <Descriptions.Item label={translateX('price_notes')}>
                      {pricing.priceNotes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Usage Info Section */}
      {introTypingComplete && tp.usage && hasUsage && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['usage']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'usage',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <ToolOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('usage_info')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {usage.commonServices?.length > 0 && (
                    <Descriptions
                      bordered
                      size="small"
                      column={1}
                      layout={isMobile ? 'vertical' : 'horizontal'}
                      styles={{ label: { fontWeight: 500 } }}
                      style={{ marginBottom: usage.alternatives?.length > 0 ? 16 : 0 }}
                    >
                      <Descriptions.Item label={translateX('common_services')}>
                        <Flex gap={4} wrap="wrap">
                          {usage.commonServices.map((svc, i) => (
                            <Tag key={i}>{svc}</Tag>
                          ))}
                        </Flex>
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                  {usage.alternatives?.length > 0 && (
                    <Descriptions
                      bordered
                      size="small"
                      column={1}
                      layout={isMobile ? 'vertical' : 'horizontal'}
                      styles={{ label: { fontWeight: 500 } }}
                    >
                      <Descriptions.Item label={translateX('alternatives')}>
                        <Flex gap={4} wrap="wrap">
                          {usage.alternatives.map((alt, i) => (
                            <Tag key={i} color="blue">{alt}</Tag>
                          ))}
                        </Flex>
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                  {usage.usageNotes && (
                    <div style={{ marginTop: 12, lineHeight: 1.6, color: 'var(--ant-color-text-secondary)' }}>
                      {usage.usageNotes}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Maintenance Section */}
      {introTypingComplete && tp.maintenance && hasMaintenance && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['maintenance']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'maintenance',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <ClockCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('maintenance')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {maintenance.expectedLifespan && (
                    <Descriptions.Item label={translateX('expected_lifespan')}>
                      {maintenance.expectedLifespan}
                    </Descriptions.Item>
                  )}
                  {maintenance.calibration && (
                    <Descriptions.Item label={translateX('calibration')}>
                      {maintenance.calibration}
                    </Descriptions.Item>
                  )}
                  {maintenance.maintenanceNotes && (
                    <Descriptions.Item label={translateX('maintenance_notes')}>
                      {maintenance.maintenanceNotes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* References - Videos */}
      {introTypingComplete && tp.references && refs?.videos?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['videos']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'videos',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <PlayCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('recommended_videos')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {refs.videos.map((video, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < refs.videos.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      {video.url ? (
                        <a href={video.url} target="_blank" rel="noopener noreferrer">
                          <Text strong style={{ fontSize: 13 }}>{video.title}</Text>
                        </a>
                      ) : (
                        <Text strong style={{ fontSize: 13 }}>{video.title}</Text>
                      )}
                      {video.channel && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {video.channel}
                        </Text>
                      )}
                      {video.topic && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                          {video.topic}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* References - Websites */}
      {introTypingComplete && tp.references && refs?.websites?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['websites']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'websites',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <GlobalOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('reference_websites')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {refs.websites.map((site, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < refs.websites.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      {site.url ? (
                        <a href={site.url} target="_blank" rel="noopener noreferrer">
                          <Text strong style={{ fontSize: 13 }}>{site.name}</Text>
                        </a>
                      ) : (
                        <Text strong style={{ fontSize: 13 }}>{site.name}</Text>
                      )}
                      {site.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {site.description}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* References - Search Terms */}
      {introTypingComplete && tp.references && refs?.searchTerms?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['search-terms']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'search-terms',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <SearchOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('search_terms')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <Flex gap={4} wrap="wrap">
                    {refs.searchTerms.map((term, i) => (
                      <Tag key={i} color="processing">{term}</Tag>
                    ))}
                  </Flex>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Meta info and Edit link */}
      {introTypingComplete && tp.meta && meta && (
        <div className="fade-in" style={{ marginTop: 16 }}>
          <Divider style={{ margin: '12px 0' }} />
          <Flex
            vertical={isMobile}
            justify={isMobile ? 'center' : 'space-between'}
            align="center"
            wrap="wrap"
            gap={isMobile ? 12 : 16}
          >
            {/* Meta stats */}
            <Flex gap={isMobile ? 16 : 24} wrap="wrap" justify={isMobile ? 'center' : 'flex-start'}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{translateX('model')}:</span> {meta.model}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>Tokens:</span>{' '}
                {meta.totalTokens?.toLocaleString()}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{translateX('time')}:</span>{' '}
                {(meta.responseTimeMs / 1000).toFixed(1)}s
              </Text>
              {creditsDebit?.debited !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('credits_debited')}:</span>{' '}
                  {creditsDebit.debited}
                </Text>
              )}
              {creditsInfo?.availableCredits !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('current_balance')}:</span>{' '}
                  {creditsInfo.availableCredits}
                </Text>
              )}
            </Flex>

            {/* Edit Tool link */}
            {tp.editLink && catalogedToolId && (
              <Flex gap={16} align="center" justify={isMobile ? 'center' : 'flex-end'}>
                <a
                  onClick={() => onEditTool(catalogedToolId)}
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: isMobile ? 16 : 0,
                  }}
                  className="fade-in"
                >
                  <EditOutlined />
                  {translateX('edit_tool')}
                </a>
              </Flex>
            )}
          </Flex>
        </div>
      )}

      {/* Progressive phase indicator - shows next section being "processed" */}
      {introTypingComplete && renderRevealPhase(tp)}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SearchedTool;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Sections reveal order mapped to phase messages.
 * Each entry: what section must appear BEFORE this phase text shows,
 * and what the phase text says while "processing" the NEXT section.
 */
const REVEAL_PHASES = [
  { after: null,            phaseKey: 'ai_phase_analyzing_tool',         icon: SearchOutlined },
  { after: 'name',          phaseKey: 'ai_phase_classifying_tool',       icon: TagsOutlined },
  { after: 'classification', phaseKey: 'ai_phase_checking_specs',       icon: SettingOutlined },
  { after: 'specs',         phaseKey: 'ai_phase_searching_prices',       icon: DollarOutlined },
  { after: 'pricing',       phaseKey: 'ai_phase_analyzing_usage',        icon: ToolOutlined },
  { after: 'usage',         phaseKey: 'ai_phase_checking_maintenance',   icon: ClockCircleOutlined },
  { after: 'maintenance',   phaseKey: 'ai_phase_compiling_references',   icon: GlobalOutlined },
  { after: 'references',    phaseKey: 'ai_phase_finalizing',             icon: CheckCircleOutlined },
];

/**
 * Render the current reveal phase indicator with shimmer effect.
 * Shows what's "being processed" next, disappears when all sections are revealed.
 */
function renderRevealPhase(tp) {
  // Find the current phase: the first entry whose "after" section is revealed
  // but whose NEXT section is NOT yet revealed
  let currentPhase = null;

  for (const phase of REVEAL_PHASES) {
    // Check if the prerequisite section is revealed (or null for the first)
    const prerequisiteMet = phase.after === null || tp[phase.after];
    if (!prerequisiteMet) break;
    currentPhase = phase;
  }

  // If meta is revealed, all done - no more phase indicator
  if (!currentPhase || tp.meta) return null;

  const PhaseIcon = currentPhase.icon;

  return (
    <div className="fade-in" style={{ marginTop: 16, marginBottom: 8 }}>
      <span className="shimmer-text" style={{ fontSize: 14 }}>
        <PhaseIcon style={{ fontSize: 14 }} />
        <span>{translateX(currentPhase.phaseKey)}...</span>
      </span>
    </div>
  );
}
